import { TutorialStep } from '../hooks/useTutorial';
import { getBrandingColor, getBrandingColorRgba } from '../utils/brandingStyles';

// Helper function to get tutorial styles with tenant color
const getTutorialStyles = () => {
  const primaryColor = getBrandingColor();
  return {
    options: {
      primaryColor,
      backgroundColor: '#1F1F21',
      textColor: '#ffffff',
      arrowColor: primaryColor,
      overlayColor: 'rgba(0, 0, 0, 0.8)'
    },
    tooltip: {
      backgroundColor: '#1F1F21',
      color: '#ffffff',
      border: `1px solid ${primaryColor}`
    },
    tooltipTitle: {
      color: primaryColor
    },
    buttonNext: {
      backgroundColor: primaryColor,
      color: '#ffffff'
    },
    buttonBack: {
      backgroundColor: 'transparent',
      border: `1px solid ${primaryColor}`,
      color: primaryColor
    },
    buttonSkip: {
      color: '#ffffff'
    }
  };
};

// Tutorial steps for the Home page
export const homeTutorialSteps = (t: (key: string) => string): TutorialStep[] => [
  {
    target: '.tutorial-welcome',
    content: t('tutorial.home.welcome'),
    title: t('tutorial.home.welcomeTitle'),
    placement: 'center',
    disableBeacon: true,
    hideCloseButton: true,
    styles: getTutorialStyles()
  },
  {
    target: '[data-tutorial="discover-process"]',
    content: t('tutorial.home.discoverProcess'),
    title: t('tutorial.home.discoverProcessTitle'),
    placement: 'bottom',
    spotlightClicks: true,
  },
  {
    target: '[data-tutorial="generate-diagram"]',
    content: t('tutorial.home.generateDiagram'),
    title: t('tutorial.home.generateDiagramTitle'),
    placement: 'bottom',
    spotlightClicks: true,
  },
  {
    target: '[data-tutorial="data-collect"]',
    content: t('tutorial.home.collectData'),
    title: t('tutorial.home.collectDataTitle'),
    placement: 'bottom',
    spotlightClicks: true,
  },
  {
    target: '[data-tutorial="analyze-optimize"]',
    content: t('tutorial.home.analyzeOptimize'),
    title: t('tutorial.home.analyzeOptimizeTitle'),
    placement: 'bottom',
    spotlightClicks: true,
  },
  {
    target: '[data-tutorial="design-future"]',
    content: t('tutorial.home.designFuture'),
    title: t('tutorial.home.designFutureTitle'),
    placement: 'bottom',
    spotlightClicks: true,
  },
  {
    target: '[data-tutorial="recent-processes"]',
    content: t('tutorial.home.recentProcesses'),
    title: t('tutorial.home.recentProcessesTitle'),
    placement: 'top',
  }
];

// Tutorial steps for the Process Creation page
export const processCreateTutorialSteps = (t: (key: string) => string): TutorialStep[] => [
  {
    target: '.tutorial-process-create-welcome',
    content: t('tutorial.processCreate.welcome'),
    title: t('tutorial.processCreate.welcomeTitle'),
    placement: 'center',
    disableBeacon: true,
    hideCloseButton: true,
    styles: getTutorialStyles()
  },
  {
    target: '[data-tutorial="process-name"]',
    content: t('tutorial.processCreate.processName'),
    title: t('tutorial.processCreate.processNameTitle'),
    placement: 'bottom',
  },
  {
    target: '[data-tutorial="process-description"]',
    content: t('tutorial.processCreate.processDescription'),
    title: t('tutorial.processCreate.processDescriptionTitle'),
    placement: 'bottom',
  },
  {
    target: '[data-tutorial="process-industry"]',
    content: t('tutorial.processCreate.processIndustry'),
    title: t('tutorial.processCreate.processIndustryTitle'),
    placement: 'bottom',
  },
  {
    target: '[data-tutorial="process-language"]',
    content: t('tutorial.processCreate.processLanguage'),
    title: t('tutorial.processCreate.processLanguageTitle'),
    placement: 'bottom',
  },
  {
    target: '[data-tutorial="process-type"]',
    content: t('tutorial.processCreate.processType'),
    title: t('tutorial.processCreate.processTypeTitle'),
    placement: 'top',
    // This step will be skipped if the element doesn't exist (AI mode)
  },
  {
    target: '[data-tutorial="create-button"]',
    content: t('tutorial.processCreate.createButton'),
    title: t('tutorial.processCreate.createButtonTitle'),
    placement: 'top',
  }
];

// Tutorial steps for the Campaigns page
export const campaignsTutorialSteps = (t: (key: string) => string): TutorialStep[] => [
  {
    target: '.tutorial-campaigns-welcome',
    content: t('tutorial.campaigns.welcome'),
    title: t('tutorial.campaigns.welcomeTitle'),
    placement: 'center',
    disableBeacon: true,
    hideCloseButton: true,
    styles: getTutorialStyles()
  },
  {
    target: '[data-tutorial="campaign-navigation"]',
    content: t('tutorial.campaigns.navigation'),
    title: t('tutorial.campaigns.navigationTitle'),
    placement: 'right',
  },
  {
    target: '[data-tutorial="campaign-main-view"]',
    content: t('tutorial.campaigns.mainView'),
    title: t('tutorial.campaigns.mainViewTitle'),
    placement: 'bottom',
  },
  {
    target: '[data-tutorial="ai-assistant"]',
    content: t('tutorial.campaigns.aiAssistant'),
    title: t('tutorial.campaigns.aiAssistantTitle'),
    placement: 'top',
  },
  {
    target: '[data-tutorial="campaign-sidebar"]',
    content: t('tutorial.campaigns.sidebar'),
    title: t('tutorial.campaigns.sidebarTitle'),
    placement: 'left',
  },
  {
    target: '[data-tutorial="campaign-status"]',
    content: t('tutorial.campaigns.status'),
    title: t('tutorial.campaigns.statusTitle'),
    placement: 'left',
  },
  {
    target: '[data-tutorial="main-view-tabs"]',
    content: t('tutorial.campaigns.viewTabs'),
    title: t('tutorial.campaigns.viewTabsTitle'),
    placement: 'left',
  }
];

// Tutorial steps for the Analysis page (Simplified: 5 steps)
export const analysisTutorialSteps = (t: (key: string) => string): TutorialStep[] => [
  {
    target: '.tutorial-analysis-welcome',
    content: t('tutorial.analysis.welcome'),
    title: t('tutorial.analysis.welcomeTitle'),
    placement: 'center',
    disableBeacon: true,
    hideCloseButton: true,
    styles: getTutorialStyles()
  },
  {
    target: '[data-tutorial="new-document-button"]',
    content: t('tutorial.analysis.newDocument'),
    title: t('tutorial.analysis.newDocumentTitle'),
    placement: 'right',
  },
  {
    target: '[data-tutorial="analysis-suggestions"]',
    content: t('tutorial.analysis.suggestions'),
    title: t('tutorial.analysis.suggestionsTitle'),
    placement: 'top',
  },
  {
    target: '[data-tutorial="analysis-input"]',
    content: t('tutorial.analysis.input'),
    title: t('tutorial.analysis.inputTitle'),
    placement: 'top',
  },
  {
    target: '[data-tutorial="analysis-charts"]',
    content: t('tutorial.analysis.charts'),
    title: t('tutorial.analysis.chartsTitle'),
    placement: 'left',
  }
];

// Tutorial steps for the Flowchart Editor page (Simplified: 6 steps)
export const flowchartTutorialSteps = (t: (key: string) => string): TutorialStep[] => [
  {
    target: '.tutorial-flowchart-welcome',
    content: t('tutorial.flowchart.welcome'),
    title: t('tutorial.flowchart.welcomeTitle'),
    placement: 'center',
    disableBeacon: true,
    hideCloseButton: true,
    styles: getTutorialStyles()
  },
  {
    target: '[data-tutorial="flowchart-canvas"]',
    content: t('tutorial.flowchart.canvas'),
    title: t('tutorial.flowchart.canvasTitle'),
    placement: 'bottom',
  },
  {
    target: '[data-tutorial="flowchart-process-owner"]',
    content: t('tutorial.flowchart.processOwner'),
    title: t('tutorial.flowchart.processOwnerTitle'),
    placement: 'bottom',
    spotlightClicks: true,
  },
  {
    target: '[data-tutorial="flowchart-export-details"]',
    content: t('tutorial.flowchart.export'),
    title: t('tutorial.flowchart.exportTitle'),
    placement: 'bottom',
    spotlightClicks: true,
  },
  {
    target: '[data-tutorial="flowchart-edit"]',
    content: t('tutorial.flowchart.editMode'),
    title: t('tutorial.flowchart.editModeTitle'),
    placement: 'bottom',
    spotlightClicks: true,
  },
  {
    target: '[data-tutorial="flowchart-analysis"]',
    content: t('tutorial.flowchart.analysis'),
    title: t('tutorial.flowchart.analysisTitle'),
    placement: 'left',
    spotlightClicks: true,
  }
];

// Tutorial steps for the Admin Dashboard page
export const adminTutorialSteps = (t: (key: string) => string): TutorialStep[] => [
  {
    target: '.tutorial-admin-welcome',
    content: t('tutorial.admin.welcome'),
    title: t('tutorial.admin.welcomeTitle'),
    placement: 'center',
    disableBeacon: true,
    hideCloseButton: true,
    styles: getTutorialStyles()
  },
  {
    target: '[data-tutorial="admin-overview"]',
    content: t('tutorial.admin.overviewMerged'),
    title: t('tutorial.admin.overviewMergedTitle'),
    placement: 'bottom',
  },
  {
    target: '[data-tutorial="admin-actions"]',
    content: t('tutorial.admin.actions'),
    title: t('tutorial.admin.actionsTitle'),
    placement: 'left',
  },
  {
    target: '[data-tutorial="admin-subscription"]',
    content: t('tutorial.admin.subscription'),
    title: t('tutorial.admin.subscriptionTitle'),
    placement: 'left',
  },
  {
    target: '[data-tutorial="admin-users"]',
    content: t('tutorial.admin.users'),
    title: t('tutorial.admin.usersTitle'),
    placement: 'right',
  },
  {
    target: '[data-tutorial="admin-administrators"]',
    content: t('tutorial.admin.administrators'),
    title: t('tutorial.admin.administratorsTitle'),
    placement: 'right',
  },
  {
    target: '[data-tutorial="admin-library"]',
    content: t('tutorial.admin.library'),
    title: t('tutorial.admin.libraryTitle'),
    placement: 'right',
  }
];

// Tutorial steps for the Onboarding (Login, Signup, Setup) pages
export const onboardingTutorialSteps = (t: (key: string) => string): TutorialStep[] => [
  {
    target: '.tutorial-onboarding-welcome',
    content: t('tutorial.onboarding.welcome'),
    title: t('tutorial.onboarding.welcomeTitle'),
    placement: 'center',
    disableBeacon: true,
    hideCloseButton: true,
    styles: getTutorialStyles()
  },
  {
    target: '[data-tutorial="onboarding-login-form"]',
    content: t('tutorial.onboarding.loginForm'),
    title: t('tutorial.onboarding.loginFormTitle'),
    placement: 'top',
  },
  {
    target: '[data-tutorial="onboarding-signup-link"]',
    content: t('tutorial.onboarding.signupLink'),
    title: t('tutorial.onboarding.signupLinkTitle'),
    placement: 'top',
  },
  {
    target: '[data-tutorial="onboarding-forgot-password"]',
    content: t('tutorial.onboarding.forgotPassword'),
    title: t('tutorial.onboarding.forgotPasswordTitle'),
    placement: 'bottom',
  },
  {
    target: '[data-tutorial="onboarding-email-verification"]',
    content: t('tutorial.onboarding.emailVerification'),
    title: t('tutorial.onboarding.emailVerificationTitle'),
    placement: 'center',
  }
];

// Tutorial steps for the Library/Projects Management
export const libraryTutorialSteps = (t: (key: string) => string): TutorialStep[] => [
  {
    target: '.tutorial-library-welcome',
    content: t('tutorial.library.welcome'),
    title: t('tutorial.library.welcomeTitle'),
    placement: 'center',
    disableBeacon: true,
    hideCloseButton: true,
    styles: getTutorialStyles()
  },
  {
    target: '[data-tutorial="library-projects"]',
    content: t('tutorial.library.projects'),
    title: t('tutorial.library.projectsTitle'),
    placement: 'top',
  },
  {
    target: '[data-tutorial="library-folders"]',
    content: t('tutorial.library.folders'),
    title: t('tutorial.library.foldersTitle'),
    placement: 'right',
  },
  {
    target: '[data-tutorial="library-create-folder"]',
    content: t('tutorial.library.createFolder'),
    title: t('tutorial.library.createFolderTitle'),
    placement: 'bottom',
  },
  {
    target: '[data-tutorial="library-move-project"]',
    content: t('tutorial.library.moveProject'),
    title: t('tutorial.library.moveProjectTitle'),
    placement: 'left',
  },
  {
    target: '[data-tutorial="library-search"]',
    content: t('tutorial.library.search'),
    title: t('tutorial.library.searchTitle'),
    placement: 'bottom',
  }
];

// Tutorial steps for Support and Help
export const supportTutorialSteps = (t: (key: string) => string): TutorialStep[] => [
  {
    target: '.tutorial-support-welcome',
    content: t('tutorial.support.welcome'),
    title: t('tutorial.support.welcomeTitle'),
    placement: 'center',
    disableBeacon: true,
    hideCloseButton: true,
    styles: getTutorialStyles()
  },
  {
    target: '[data-tutorial="support-help-menu"]',
    content: t('tutorial.support.helpMenu'),
    title: t('tutorial.support.helpMenuTitle'),
    placement: 'bottom',
  },
  {
    target: '[data-tutorial="support-contact-form"]',
    content: t('tutorial.support.contactForm'),
    title: t('tutorial.support.contactFormTitle'),
    placement: 'center',
  },
  {
    target: '[data-tutorial="support-email"]',
    content: t('tutorial.support.supportEmail'),
    title: t('tutorial.support.supportEmailTitle'),
    placement: 'top',
  }
];

// Tutorial steps for Advanced Participants Management in Interviews (Simplified: 5 steps)
export const participantsTutorialSteps = (t: (key: string) => string): TutorialStep[] => [
  {
    target: '.tutorial-participants-welcome',
    content: t('tutorial.participants.welcome'),
    title: t('tutorial.participants.welcomeTitle'),
    placement: 'center',
    disableBeacon: true,
    hideCloseButton: true,
    styles: getTutorialStyles()
  },
  {
    target: '[data-tutorial="participants-sidebar"]',
    content: t('tutorial.participants.listAndStatus'),
    title: t('tutorial.participants.listAndStatusTitle'),
    placement: 'right',
  },
  {
    target: '[data-tutorial="participants-menu"]',
    content: t('tutorial.participants.menu'),
    title: t('tutorial.participants.menuTitle'),
    placement: 'left',
  },
  {
    target: '[data-tutorial="participants-replace"]',
    content: t('tutorial.participants.replace'),
    title: t('tutorial.participants.replaceTitle'),
    placement: 'bottom',
  },
  {
    target: '[data-tutorial="participants-reminder"]',
    content: t('tutorial.participants.reminder'),
    title: t('tutorial.participants.reminderTitle'),
    placement: 'bottom',
  }
];

// =============================================================================
// [TUTORIEL SAMPLE PROCESS] Guide pour le processus Employee Onboarding pré-chargé
// Parcours en 3 étapes selon la spécification technique :
// 1. Interface Interview - Capture de l'expertise métier
// 2. Visualisation Cartographie - Diagramme swimlane généré
// 3. Analyse - Insights et recommandations IA
// =============================================================================

// Styles communs pour tous les steps du tutoriel sample process - uses getTutorialStyles()

// Tutorial steps for the Sample Process (Employee Onboarding) - Simplified: 6 steps
export const sampleProcessTutorialSteps = (t: (key: string) => string): TutorialStep[] => [
  // Introduction
  {
    target: 'body',
    content: t('tutorial.sampleProcess.welcome'),
    title: t('tutorial.sampleProcess.welcomeTitle'),
    placement: 'center',
    disableBeacon: true,
    hideCloseButton: true,
    styles: getTutorialStyles()
  },
  
  // Phase 1: Interview
  {
    target: '[data-tutorial="interview-owner"]',
    content: t('tutorial.sampleProcess.interview'),
    title: t('tutorial.sampleProcess.interviewTitle'),
    placement: 'right',
    styles: getTutorialStyles()
  },
  
  // Phase 2: Navigation vers Cartographie
  {
    target: '[data-tutorial="navigation-mapping"]',
    content: t('tutorial.sampleProcess.navigationMapping'),
    title: t('tutorial.sampleProcess.navigationMappingTitle'),
    placement: 'bottom',
    spotlightClicks: true,
    styles: getTutorialStyles()
  },
  
  // Phase 3: Diagramme Swimlane
  {
    target: '[data-tutorial="mapping-swimlanes"]',
    content: t('tutorial.sampleProcess.mapping'),
    title: t('tutorial.sampleProcess.mappingTitle'),
    placement: 'top',
    styles: getTutorialStyles()
  },
  
  // Phase 4: Navigation vers Analyse
  {
    target: '[data-tutorial="navigation-analysis"]',
    content: t('tutorial.sampleProcess.navigationAnalysis'),
    title: t('tutorial.sampleProcess.navigationAnalysisTitle'),
    placement: 'bottom',
    spotlightClicks: true,
    styles: getTutorialStyles()
  },
  
  // Phase 5: Analyse IA
  {
    target: '[data-tutorial="analysis-insights"]',
    content: t('tutorial.sampleProcess.analysis'),
    title: t('tutorial.sampleProcess.analysisTitle'),
    placement: 'left',
    styles: getTutorialStyles()
  }
];

// =============================================================================
// [APP GUIDANCE] Tutoriel interactif processus Employee Onboarding
// =============================================================================

// Helper function to get App Guidance styles with tenant color
const getAppGuidanceStyles = () => {
  const primaryColor = getBrandingColor();
  return {
    options: {
      primaryColor,
      backgroundColor: '#1F1F21',
      textColor: '#ffffff',
      arrowColor: primaryColor,
      overlayColor: 'rgba(0, 0, 0, 0.85)',
      zIndex: 10000
    },
    tooltip: {
      backgroundColor: '#1F1F21',
      color: '#ffffff',
      border: `2px solid ${primaryColor}`,
      borderRadius: '12px',
      padding: '24px',
      maxWidth: '800px',
      minWidth: '350px',
      boxShadow: `0 10px 40px ${getBrandingColorRgba(0.4)}`,
      position: 'relative' as const
    },
    tooltipContainer: {
      textAlign: 'left' as const
    },
    tooltipTitle: {
      color: primaryColor,
      fontSize: '20px',
      fontWeight: 'bold',
      marginBottom: '14px'
    },
    tooltipContent: {
      fontSize: '15px',
      lineHeight: '1.7',
      color: '#ffffff'
    },
    tooltipFooter: {
      marginTop: '16px'
    },
    buttonNext: {
      backgroundColor: primaryColor,
      color: '#ffffff',
      border: 'none',
      borderRadius: '8px',
      padding: '12px 24px',
      fontWeight: 'bold',
      fontSize: '14px',
      cursor: 'pointer'
    },
    buttonBack: {
      backgroundColor: 'transparent',
      border: `2px solid ${primaryColor}`,
      color: primaryColor,
      borderRadius: '8px',
      padding: '12px 24px',
      marginRight: '10px',
      cursor: 'pointer'
    },
    buttonSkip: {
      color: '#999999',
      fontSize: '13px',
      cursor: 'pointer'
    },
    buttonClose: {
      color: '#ffffff',
      cursor: 'pointer'
    },
    spotlight: {
      borderRadius: '8px'
    }
  };
};

// Tutorial steps for Employee Onboarding App Guidance - Simplified: 6 steps
export const employeeOnboardingGuidanceSteps = (t: (key: string) => string): TutorialStep[] => [
  // Introduction
  {
    target: 'body',
    content: t('tutorial.appGuidance.welcome'),
    title: t('tutorial.appGuidance.welcomeTitle'),
    placement: 'center',
    disableBeacon: true,
    hideCloseButton: false,
    styles: getAppGuidanceStyles()
  },

  // Phase Interview
  {
    target: '[data-tutorial="interview-stakeholders"]',
    content: t('tutorial.appGuidance.interview'),
    title: t('tutorial.appGuidance.interviewTitle'),
    placement: 'right',
    spotlightClicks: true,
    styles: getAppGuidanceStyles()
  },

  // Zone de conversation avec l'IA
  {
    target: '[data-tutorial="interview-questions"]',
    content: t('tutorial.appGuidance.interviewAction'),
    title: t('tutorial.appGuidance.interviewActionTitle'),
    placement: 'left',
    spotlightClicks: true,
    styles: getAppGuidanceStyles()
  },

  // Diagramme Mapping (navigation)
  {
    target: '[data-tutorial="diagram-list-section"]',
    content: t('tutorial.appGuidance.mapping'),
    title: t('tutorial.appGuidance.mappingTitle'),
    placement: 'right',
    spotlightClicks: true,
    disableScrolling: true,
    disableOverlayClose: true,
    spotlightPadding: 0,
    floaterProps: {
      disableFlip: true,
      options: {
        preventOverflow: {
          enabled: false
        }
      }
    },
    styles: getAppGuidanceStyles()
  },

  // CTA final
  {
    target: '[data-tutorial="create-new-process"]',
    content: t('tutorial.appGuidance.analysisCtaButtonTitle'),
    title: t('tutorial.appGuidance.analysisCtaButton'),
    placement: 'right',
    spotlightClicks: true,
    styles: getAppGuidanceStyles()
  }
];
